import * as Sentry from '@sentry/react';
import { getTenantSnapshot } from '@auw/onprem';
import { resolveTenantAnalyticsSentry } from '@auw/tenant';
import { setErrorReporter } from '@auw/workspace-ui';

let sentryInitialized = false;

const PII_PATTERNS = /token|password|secret|authorization|cookie|api[_-]?key|credentials|refresh|csrf/i;

function stripPiiFromUrl(url: string): string {
  try {
    const u = new URL(url);
    for (const key of [...u.searchParams.keys()]) {
      if (PII_PATTERNS.test(key) || key === 'code' || key === 'state') {
        u.searchParams.set(key, '[FILTERED]');
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.request?.url) {
    event.request.url = stripPiiFromUrl(event.request.url);
  }

  if (event.request?.headers) {
    for (const key of Object.keys(event.request.headers)) {
      if (PII_PATTERNS.test(key)) {
        event.request.headers[key] = '[FILTERED]';
      }
    }
  }

  if (event.request?.cookies) {
    delete event.request.cookies;
  }

  if (event.request?.query_string) {
    event.request.query_string = stripPiiFromUrl(`http://x?${event.request.query_string}`).split('?')[1] ?? '';
  }

  delete event.user?.email;
  delete event.user?.ip_address;
  delete event.user?.username;

  if (event.breadcrumbs) {
    for (const bc of event.breadcrumbs) {
      if (bc.data?.url && typeof bc.data.url === 'string') {
        bc.data.url = stripPiiFromUrl(bc.data.url);
      }
    }
  }

  return event;
}

/**
 * Phase 8 — tenant-aware Sentry init.
 *
 * Primary gate: tenant manifest (`analytics.sentry.enabled`). On-prem
 * tenants that omit the block or set it to `false` never call
 * `Sentry.init` and therefore never establish a Sentry transport.
 * Secondary gate: DSN string (tenant override or `VITE_SENTRY_DSN`
 * env var) and `import.meta.env.PROD` — identical to pre-Phase-8.
 *
 * `allowUrls` defaults to a regex derived from the tenant's `siteUrl`
 * so enterprise installs automatically scope event capture to their
 * own origin without custom code.
 */
export function initErrorMonitoring() {
  if (sentryInitialized) return;

  const tenant = getTenantSnapshot();
  const sentryConfig = tenant ? resolveTenantAnalyticsSentry(tenant) : null;
  // When no tenant snapshot is available yet (pre-bootstrap or tests)
  // fall back to pre-Phase-8 env-only wiring so B2C behaviour remains
  // identical. When a tenant *is* available, disabled flag wins.
  if (tenant && sentryConfig === null) return;

  const dsn =
    (sentryConfig?.dsn && sentryConfig.dsn.trim()) ||
    (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
    '';
  if (!dsn) return;

  const tracesSampleRateRaw = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0');
  const tracesSampleRate = Number.isFinite(tracesSampleRateRaw) ? tracesSampleRateRaw : 0;

  const allowUrls = resolveAllowUrls(sentryConfig?.allowUrls, tenant?.identity.siteUrl);

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_APP_ENV as string | undefined) || import.meta.env.MODE,
    tracesSampleRate,
    enabled: import.meta.env.PROD,
    beforeSend: scrubEvent,
    sendDefaultPii: false,
    allowUrls,
  });

  /* Phase 11-4f: expose Sentry to shared workspace-ui error boundaries
   * via the DI seam so `@auw/workspace-ui` stays vendor-agnostic. */
  setErrorReporter({
    captureException: (error, options) => {
      Sentry.captureException(error, {
        tags: options?.tags as Record<string, string> | undefined,
        extra: options?.extra,
      });
    },
  });

  sentryInitialized = true;
}

function resolveAllowUrls(
  configured: readonly string[] | undefined,
  tenantSiteUrl: string | undefined,
): RegExp[] {
  const sources: string[] = configured && configured.length > 0 ? [...configured] : [];
  if (sources.length === 0) {
    if (tenantSiteUrl) {
      try {
        const host = new URL(tenantSiteUrl).hostname.replace(/^www\./, '');
        sources.push(host.replace(/\./g, '\\.'));
      } catch {
        // Fallback below.
      }
    }
    if (sources.length === 0) sources.push('askyour\\.ai');
  }
  return sources
    .map((src) => {
      try {
        return new RegExp(src);
      } catch {
        return null;
      }
    })
    .filter((r): r is RegExp => r !== null);
}
